/**
 * Files a generated PDF in an investor's Your Docs (#6503).
 *
 * The backend (auth/upload-to-your-docs) copies the PDF to S3 first, then appends the document record in one
 * write, so a failed upload can't leave a phantom document and two uploads can't drop each other's record.
 * This helper only decides the document's labels for each callOrigin, then updates the session copies.
 */
let proxyUserName;

const UPLOAD_TO_YOUR_DOCS_URL = 'https://api.nthround.io/portal/auth/upload-to-your-docs';

/**
 * POST to upload-to-your-docs; the stored record ({ pdf_url, doc }). A slow PDF service fails the call within
 * seconds, so it's tried twice (3s apart); each try can take ~29s (the gateway's limit). Never again after a 504: the gateway gave up but the
 * save may still finish, and another try would file the document twice. Throws the last error.
 */
async function postToYourDocs(body, { onRetry } = {}) {
	for (let attempt = 1; ; attempt++) {
		try {
			const res = await axios.post(UPLOAD_TO_YOUR_DOCS_URL, body);
			if (!res?.data?.pdf_url) throw new Error('upload-to-your-docs returned no pdf_url');
			return res.data;
		} catch (err) {
			if (attempt === 2 || err?.response?.status === 504) throw err;
			console.warn(`upload-to-your-docs failed (try ${attempt}), trying again`, err?.message);
			onRetry?.(attempt);
			await new Promise((r) => setTimeout(r, 3000));
		}
	}
}

async function uploadToYourDocs({
	thisType = null,
	pdfUrl,
	name = null,
	investor = null,
	quarter = null, //only passed in from campaign.js
	recipient = null,
	recipients = null,
	role = null, //only passed when called from trade.js
	roundQuantity = null, //only passed when called from trade.js
	totalPrice = null, //only passed when called from trade.js
	callOrigin = null,
	theseAlloc = [], //only passed in from ledgers.js
	msgCount = null,
	proxySignerName = null, //only passed when called from index indexProxySigned
	members = null, //only passed when called from index indexProxySigned
	firstDocId = null, //only passed when called from index indexProxySigned
	formType = null,
} = {}) {
	let holderData;
	let analyticsEventLabel;
	let docName;
	let docDescription;
	let docType;
	let docTag;
	let docDate = moment().format('MMM D, YYYY');
	let docId = firstDocId ? firstDocId : Date.now().toString();
	let receiver = investor;

	switch (callOrigin) {
		/** Labels (and whose record) for each origin of the call to uploadToYourDocs */
		case 'indexAcknowledgementUploaded': // used once for Alera, leaving for now for reference
			holderData = userData.clients[client];
			receiver = userData.userid;
			docName = name;
			docDescription = 'For ' + userData.user_name + ' dated ' + docDate;
			docType = 'Agreement';
			docTag = '';
			analyticsEventLabel = 'Generated Acknowledgement';
			break;
		case 'indexProxySigned':
			holderData = members[members.findIndex((c) => c.userid == investor)];
			docName = name.replace('.pdf', '').trim();
			docDescription = 'For ' + proxyUserName + ' signed by ' + document.getElementById('signaturePreview')?.innerHTML;
			docType = 'Agreement';
			docTag = client.startsWith('onward') ? 'ONW' : 'C';
			analyticsEventLabel = 'Shareholder Document Added';
			break;
		case 'trade': // @Chris triage needed for testing
			holderData = userData.clients[client];
			docName = name.substring(0, name.indexOf(' for '));
			docDescription =
				(role == 'Seller' ? 'Sold ' : 'Purchased ') +
				roundQuantity +
				' units for ' +
				totalPrice +
				' (' +
				Number(thisType.sharePrice).toLocaleString('en-US', m2) +
				' per unit)';
			docType = 'Agreement';
			docTag = '';
			analyticsEventLabel = 'IoI Confirmation Added';
			break;
		case 'enroll': // @Chris triage needed for testing
			holderData = userData.clients[client];
			docName = name;
			docDescription = 'For ' + userData.user_name + ' dated ' + docDate;
			docType = 'Agreement';
			docTag = '';
			analyticsEventLabel = 'Shareholder Document Added';
			break;
		case 'campaign':
			holderData = capTableData.find((x) => investor == x.userid).clients[client];
			docName = 'Campaign Template';
			docDescription = DOMPurify.sanitize(recipients[investor].sum.toLocaleString('en-US', usd));
			docType = 'Call/Confirmation';
			docTag = 'NR';
			analyticsEventLabel = 'Campaign Template Added';
			break;
		case 'payments':
			holderData = capTableData.find((x) => investor == x.userid).clients[client];
			docName = 'Recipient Confirmation for ' + quarter;
			docDescription = DOMPurify.sanitize(recipients[investor].sum.toLocaleString('en-US', usd));
			docType = 'Call/Confirmation';
			docTag = 'NR';
			analyticsEventLabel = 'Shareholder Document Added';
			break;
		case 'ledgersDocUploaded':
			holderData = capTableData.find((x) => investor == x.userid).clients[client];
			docName = name.replace('.pdf', '').trim();
			docDescription = '$' + theseAlloc[investor][1].toLocaleString();
			docType = 'Call/Notice';
			docTag = thisType.shareClass;
			analyticsEventLabel = 'Ledger Notice Added';
			break;
		case 'ledgersAgreementGenerated':
			holderData = capTableData.find((x) => investor == x.userid).clients[client];
			receiver = thisType.receiver;
			docName = thisType.shareClass + ' Agreement';
			docDescription = 'For ' + recipient + ' dated ' + docDate;
			docType = 'Agreement';
			docTag = thisType.shareClass;
			analyticsEventLabel = 'Shareholder Document Added';
			break;
		case 'proxy-voting':
			holderData = userData.clients[client];
			docName = name.split('_vote')[0];
			docDescription = 'For ' + recipient + ' signed by ' + proxySignerName;
			docType = 'Agreement';
			docTag = '';
			analyticsEventLabel = 'Proxy Voting Form Added';
			break;
		case 'signature':
			holderData = userData.clients[client];
			docName = name.replace('.pdf', '');
			docDescription = '';
			docType = 'Agreement';
			docTag = '';
			analyticsEventLabel = 'Signature Document Added';
			break;
		case 'survey':
			holderData = userData.clients[client];
			docName = name.replace('.pdf', '');
			docDescription = proxySignerName ? 'Signed by ' + proxySignerName : '';
			docType = 'Agreement';
			docTag = '';
			analyticsEventLabel = 'Survey Response Added';
			break;
		case 'formsAutoJoined':
		case 'forms':
			if (!userData.clients) userData.clients = {};
			if (!userData.clients[client]) userData.clients[client] = {};
			holderData = userData.clients[client];
			docName = name.replace('.pdf', '');
			docDescription = userData.user_name ? 'for ' + userData.user_name : '';
			docType = formType == 'W-9' ? 'Tax Form' : 'Agreement';
			docTag = '';
			analyticsEventLabel = callOrigin == 'formsAutoJoined' ? 'Prospect Auto-Joined' : 'Shareholder Document Added';
			break;
		case 'tax-forms': {
			const holder = capTableData.find((x) => investor == x.userid);
			holderData = holder.clients[client];
			docName = name.replace('.pdf', '');
			docDescription = holder.user_name ? 'for ' + holder.user_name : '';
			docType = 'Tax Form';
			docTag = '';
			analyticsEventLabel = 'Tax Form Added';
			break;
		}
		default:
			console.error(`uploadToYourDocs: unknown callOrigin "${callOrigin}"`);
			return { success: false };
	}

	// One call: the backend stores the PDF, then records the document
	let stored;
	try {
		stored = await postToYourDocs(
			JSON.stringify({
				client,
				userid: authedUser,
				cookie,
				investor: receiver,
				url: pdfUrl,
				name: docName,
				description: docDescription,
				type: docType,
				tag: docTag,
				date: docDate,
				id: docId,
				callOrigin,
			})
		);
	} catch (error) {
		console.error('uploadToYourDocs failed', callOrigin, receiver, error);
	}
	if (!stored?.pdf_url) return { success: false };

	// Session copies follow what the backend stored
	const newDoc = stored.doc || { name: docName, description: docDescription, require2Factor: false, type: docType, tag: docTag, date: docDate, id: docId };
	if (holderData) updateDocuments(holderData, newDoc, callOrigin, investor);

	if (callOrigin === 'indexPaymentConfirmationDocGenerated') {
		const paySelectionNow = document.getElementById('paySelectionNow');
		document.body.style.cursor = 'default';
		document.body.style.pointerEvents = null;
		paySelectionNow.classList.remove('disabled');
		paySelectionNow.innerHTML = 'VIEW CONFIRMATION';
	}
	if (callOrigin === 'indexAcknowledgementUploaded') {
		M.Modal.getInstance(document.getElementById('declineModal')).close();
		window.location.replace('/');
	}

	const fileType = String(newDoc.url || '.pdf')
		.split('.')
		.pop();
	if (callOrigin === 'trade') trackAnalytics(analyticsEventLabel, { nrid: thisType.nrid, sender: userData.userid });
	else trackAnalytics(analyticsEventLabel, { documentTitle: newDoc.name, documentFileType: fileType });

	return { success: true, pdfUrl: stored.pdf_url, doc: newDoc };
}

function trackAnalytics(eventLabel, additionalData) {
	if (window.analytics) window.analytics.track(eventLabel, { userEmail: authedUser, workspace: client, ...additionalData });
}

/** Mirror the stored document into the session copies the current page reads */
function updateDocuments(holderData, newDoc, callOrigin, investor) {
	if (!holderData.documents) holderData.documents = [];
	if (!holderData.documents.some((d) => d.id == newDoc.id)) holderData.documents.push(newDoc);

	if (callOrigin === 'indexProxySigned') {
		if (userData.userid == investor) {
			userData.clients[client].documents = holderData.documents;
			updateSessionStorage('user.Data', userData);
		}
	} else if (['signature', 'proxy-voting', 'survey'].includes(callOrigin)) {
		// authedUser may be an observer or an admin previewing the account
		userData.clients[client] = holderData;
		if (authedUser != investor) {
			const shareholderData = JSON.parse(sessionStorage.getItem('shareholder.Data') || 'null');
			if (shareholderData?.clients?.[client]) {
				if (!shareholderData.clients[client].documents) shareholderData.clients[client].documents = [];
				shareholderData.clients[client].documents.push(newDoc);
				updateSessionStorage('shareholder.Data', shareholderData);
			}
		}
		const capTable = NthUtil.sessionStore.readCapTable(client);
		const i = capTable ? capTable.findIndex((u) => u.userid == investor) : -1;
		if (i >= 0) {
			capTable[i].clients[client].documents = holderData.documents;
			NthUtil.sessionStore.updateCapTable(client, capTable);
		}
		updateSessionStorage('user.Data', userData);
	} else if (authedUser == investor && callOrigin !== 'indexAcknowledgementUploaded') {
		const user = JSON.parse(sessionStorage.getItem('user.Data') || 'null');
		if (user?.clients) {
			user.clients[client] = holderData;
			updateSessionStorage('user.Data', user);
		}
	}
	if (!['proxy-voting', 'tax-forms'].includes(callOrigin)) document.dispatchEvent(new Event('RefreshCapTable'));
}

function updateSessionStorage(key, data) {
	try {
		sessionStorage.setItem(key, JSON.stringify(data));
	} catch (error) {
		console.error('Error updating session storage:', error);
	}
}

/** After the retries: close the waiting dialog and say nothing was filed, with a way to start again */
function showNotSaved(text = "Your signed document couldn't be saved, so nothing was filed.", action = 'Sign again') {
	try {
		M.Modal.getInstance(document.getElementById('waitingModal'))?.close();
	} catch {}
	document.body.style.cursor = 'default';
	M.toast({
		html: `<span>${text}</span><button class="btn-flat toast-action" onclick="window.location.reload()">${action}</button>`,
		displayLength: 30000,
	});
}

export { uploadToYourDocs, postToYourDocs, showNotSaved };
